import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import Nav from "./Nav";
import {
  collection,
  getFirestore,
  onSnapshot,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Paginador from "./Paginador";
import CategoriaModal from "./CategoriaModal";
import Spinner from "./Spinner";
import { useNavContext } from "../context/NavContext";
import Breadcrumb from "./Breadcrumb";

moment.locale("es");

const Categorias = () => {
  const { teamOwnerId } = useAuth();
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25); // Valor inicial
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriaEditando, setCategoriaEditando] = useState(null);

  const handleEditarCategoria = (categoria) => {
    setCategoriaEditando(categoria); // Establece la categoría que se está editando
    setIsModalOpen(true); // Abre el modal
  };

  const handleVerDetalles = (categoria) => {
    const nombreUrl = encodeURIComponent(categoria.nombre.trim());
    navigate(`/categorias/${nombreUrl}/productos`);
  };

  const handleOpenModal = () => {
    setCategoriaEditando(null); // Asegúrate de que no hay categoría editando
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSubmitCategoria = async (newCategoria) => {
    const firestore = getFirestore();
    const basePath = ["usuarios", teamOwnerId, "categorias"];

    // ► normalizamos el nombre
    const nombreNormalized = (newCategoria.nombre || "").trim().toUpperCase();

    if (newCategoria.id) {
      /* ─── EDICIÓN ──────────────────────────────────────────── */
      // estado local optimista
      setCategorias((prev) =>
        prev.map((cat) =>
          cat.id === newCategoria.id
            ? {
                ...cat,
                nombre: nombreNormalized,
                descripcion: newCategoria.descripcion,
              }
            : cat
        )
      );
      handleCloseModal();

      try {
        const categoriaDocRef = doc(firestore, ...basePath, newCategoria.id);

        let imageUrl = newCategoria.imagenUrl || "";
        if (newCategoria.imagen && typeof newCategoria.imagen !== "string") {
          const storage = getStorage();
          const imageRef = ref(
            storage,
            `categorias/${newCategoria.imagen.name}`
          );
          const snapshot = await uploadBytes(imageRef, newCategoria.imagen);
          imageUrl = await getDownloadURL(snapshot.ref);
        }

        await updateDoc(categoriaDocRef, {
          nombre: nombreNormalized,
          descripcion: newCategoria.descripcion || "",
          imagenUrl: imageUrl || "",
        });

        // reflejar URL en el estado
        setCategorias((prev) =>
          prev.map((cat) =>
            cat.id === newCategoria.id ? { ...cat, imagenUrl: imageUrl } : cat
          )
        );

        Swal.fire({
          icon: "success",
          title: "¡Categoría actualizada!",
          timer: 1500,
        });
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: "error", title: "Error al actualizar categoría." });
      }
    } else {
      /* ─── NUEVA ─────────────────────────────────────────────── */
      const tempId = `temp-${Date.now()}`;
      setCategorias((prev) => [
        ...prev,
        {
          id: tempId,
          nombre: nombreNormalized,
          descripcion: newCategoria.descripcion || "",
          imagenUrl: "",
        },
      ]);
      handleCloseModal();

      try {
        const categoriaRef = collection(firestore, ...basePath);

        let imageUrl = "";
        if (newCategoria.imagen && typeof newCategoria.imagen !== "string") {
          const storage = getStorage();
          const imageRef = ref(
            storage,
            `categorias/${newCategoria.imagen.name}`
          );
          const snapshot = await uploadBytes(imageRef, newCategoria.imagen);
          imageUrl = await getDownloadURL(snapshot.ref);
        }

        const docRef = await addDoc(categoriaRef, {
          nombre: nombreNormalized,
          descripcion: newCategoria.descripcion || "",
          imagenUrl: imageUrl || "",
        });

        // reemplazar temporal por definitivo
        setCategorias((prev) =>
          prev.map((cat) =>
            cat.id === tempId
              ? {
                  ...cat,
                  id: docRef.id,
                  nombre: nombreNormalized,
                  imagenUrl: imageUrl,
                }
              : cat
          )
        );

        Swal.fire({
          icon: "success",
          title: "¡Categoría agregada!",
          timer: 1500,
        });
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: "error", title: "Error al agregar categoría." });
        setCategorias((prev) => prev.filter((cat) => cat.id !== tempId));
      }
    }
  };

  // ──────────────────────────────────────────────────────────────
  // FUNCIÓN: handleDeleteSelected (sin cambios de lógica)
  // ──────────────────────────────────────────────────────────────
  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará las categorías seleccionadas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (!confirmed.isConfirmed) return;

      const firestore = getFirestore();
      const basePath = ["usuarios", teamOwnerId, "categorias"];
      const deletedIds = [];

      for (const categoriaId of selectedCategorias) {
        await deleteDoc(doc(firestore, ...basePath, categoriaId));
        deletedIds.push(categoriaId);
      }

      setCategorias((prev) =>
        prev.filter((cat) => !deletedIds.includes(cat.id))
      );
      setSelectedCategorias([]);

      await Swal.fire({
        title: "¡Categorías eliminadas!",
        text: "Se eliminaron correctamente.",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    } catch (err) {
      console.error("Error al eliminar categorías:", err);
      Swal.fire({
        title: "Error",
        text: "Hubo un problema al eliminar las categorías. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    }
  };

  useEffect(() => {
    if (!teamOwnerId) return;
    setLoading(true);
    const firestore = getFirestore();
    const categoriasRef = collection(
      firestore,
      "usuarios",
      teamOwnerId, // ← en vez de user.uid
      "categorias"
    );
    const unsubscribe = onSnapshot(categoriasRef, (snapshot) => {
      setCategorias(
        snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
      setLoading(false);
    });
    return unsubscribe;
  }, [teamOwnerId]);

  const handleSelectCategoria = (categoriaId) => {
    setSelectedCategorias((prevSelected) => {
      if (prevSelected.includes(categoriaId)) {
        return prevSelected.filter((id) => id !== categoriaId);
      } else {
        return [...prevSelected, categoriaId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Si se marca el checkbox de "seleccionar todos", selecciona todas las cotizaciones
      const allCategoriasIds = filteredCategorias.map(
        (categoria) => categoria.id
      );
      setSelectedCategorias(allCategoriasIds);
    } else {
      // Si se desmarca, vacía la lista de seleccionados
      setSelectedCategorias([]);
    }
  };

  const filteredCategorias = React.useMemo(() => {
    if (!categorias || categorias.length === 0) return [];
    const searchLower = searchTerm.toLowerCase();
    return categorias.filter((categoria) => {
      const searchableFields = [
        categoria.nombre?.toLowerCase() || "",
        categoria.descripcion?.toLowerCase() || "",
      ];
      return searchableFields.some((field) => field.includes(searchLower));
    });
  }, [categorias, searchTerm]);

  const totalOrdenes = filteredCategorias.length;

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <div className="no-sticky">
        <Nav
          handleSearch={setSearchTerm}
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />

        <div className="task-filters" style={{ flexWrap: "wrap" }}>
          <div
            style={{
              flexBasis: "100%", // ocupa toda la fila
              padding: "0 4px", // mismo gutter que usas
              margin: "0 0 5px", // respirito arriba/abajo
            }}
          >
            <Breadcrumb />
          </div>

          <div className="header-actions-proyectos">
            <button className="action-button-proyectos">
              <i className="bi bi-funnel-fill"></i> Todos
              <i
                className="bi bi-chevron-down"
                style={{ fontSize: "12px" }}
              ></i>
            </button>
            <div className="buscador-pedidos">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="buscador-input"
                placeholder="Buscar categorías..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="task-filters-right">
            <div
              className="view-buttons-container"
              style={{ position: "relative" }}
            >
              <div className="delete-button-container">
                <button
                  type="button"
                  className="delete-button"
                  style={{
                    visibility:
                      selectedCategorias.length > 0 ? "visible" : "hidden",
                    opacity: selectedCategorias.length > 0 ? 1 : 0,
                    transform:
                      selectedCategorias.length > 0 ? "scale(1)" : "scale(0.9)",
                    transition:
                      "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                  }}
                  onClick={handleDeleteSelected}
                >
                  Eliminar
                </button>
              </div>
              <button className="view-button">
                <i className="bi bi-three-dots-vertical"></i>
              </button>
              <button className="exportar-button">
                <i
                  className="bi bi-cloud-arrow-down"
                  style={{ marginRight: 5 }}
                ></i>
                Exportar
              </button>
            </div>
            <div>
              <button className="new-task-button" onClick={handleOpenModal}>
                + Nueva Categoría
              </button>
            </div>
          </div>
        </div>

        <CategoriaModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitCategoria}
          categoria={categoriaEditando}
        />

        <div className="cotizaciones-table">
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              {loading ? (
                <Spinner />
              ) : filteredCategorias.length === 0 ? (
                searchTerm ? (
                  <div className="no-resultados">
                    <div
                      className="mensaje-sin-resultados"
                      style={{ marginTop: 30 }}
                    >
                      <p style={{ fontSize: 14 }}>
                        No hay resultados disponibles.
                      </p>
                    </div>
                    <div className="imagen-sin-resultados">
                      <img
                        src="/img/noSearch.svg"
                        alt="Sin resultados"
                        className="sin-resultados-imagen"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-resultados">
                    <div
                      className="mensaje-sin-resultados"
                      style={{ marginTop: 30 }}
                    >
                      <p
                        style={{
                          textAlign: "center",
                          margin: "20px 0",
                          color: "#888",
                        }}
                      >
                        No hay categorias aún.
                      </p>
                    </div>
                    <div>
                      <img
                        src="/img/no-data.svg"
                        alt="Sin resultados"
                        width={350}
                        height={350}
                      />
                    </div>
                  </div>
                )
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          className="checkbox-select-all check"
                        />
                      </th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategorias.map((categoria) => (
                      <tr key={categoria.id}>
                        <td className="check">
                          <input
                            type="checkbox"
                            checked={selectedCategorias.includes(categoria.id)}
                            onChange={() => handleSelectCategoria(categoria.id)}
                            className="checkbox"
                          />
                        </td>
                        <td>{categoria.nombre}</td>
                        <td>{categoria.descripcion}</td>
                        <td className="acciones-container">
                          <div className="acciones-icons">
                            <div
                              className="icon-wrapper"
                              onClick={() => handleVerDetalles(categoria)}
                            >
                              <i className="bi bi-eye"></i>
                              <span className="tooltipCategorias">
                                Mostrar Detalles
                              </span>
                            </div>
                            <div
                              className="icon-wrapper"
                              onClick={() => handleEditarCategoria(categoria)}
                            >
                              <i className="bi bi-pencil"></i>
                              <span className="tooltipCategorias">Editar</span>
                            </div>
                            <div className="icon-wrapper">
                              <i className="bi bi-shield-slash"></i>
                              <span className="tooltipCategorias">
                                Desactivar
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={totalOrdenes}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Categorias;
